<template>
    <form class="support-form" @submit.prevent="next" :style="{ height: height }">
        <transition :name="currentTransition" mode="out-in" @enter="enterStep" @before-leave="beforeLeaveStep" @after-enter="afterEnterStep">
            <div v-if="step == 'category'" key="category" class="support-form__step">
                <h3 class="support-form__step-title" aria-hidden="true">{{ $t("support.what_category") }}</h3>
                <ProSelect v-model="selectedCategoryId" required :label="$t('support.what_category')" label-hidden>
                    <option :value="null" disabled selected>{{ $t("support.category_placeholder") }}</option>
                    <option v-for="category in categories" :value="category.id">{{ category.title }}</option>
                </ProSelect>
                <p class="support-form__tip" v-if="selectedCategory && selectedCategory.tip" v-html="selectedCategory.tip"></p>
                <div class="support-form__buttons support-form__buttons--center">
                    <ProButton v-if="selectedCategory && selectedCategory.action" variant="primary" pill :href="selectedCategory.action">{{
                        selectedCategory.action_title
                    }}</ProButton>
                    <ProButton v-else variant="primary" pill type="submit">{{ $t("support.next_step") }}</ProButton>
                </div>
            </div>

            <div v-if="step == 'product'" key="product" class="support-form__step">
                <h3 class="support-form__step-title" aria-hidden="true">{{ $t("support.what_product") }}</h3>
                <ProSelect v-model="selectedProductId" required :label="$t('support.what_product')" label-hidden>
                    <option :value="null" disabled>{{ $t("support.product_placeholder") }}</option>
                    <option v-for="product in products" :value="product.id">{{ product.title }}</option>
                </ProSelect>
                <transition-group
                    class="support-form__product-options"
                    :duration="1000"
                    name="fade"
                    tag="div"
                    @enter="enterProductOption"
                    @before-leave="beforeLeaveProductOption"
                >
                    <div
                        v-for="option in selectedProduct ? selectedProduct.options : []"
                        class="support-form__product-option"
                        :key="selectedProduct.id + option.id"
                    >
                        <ProSelect :label="option.title" v-model="$data[option.id]" :help="option.help" :required="option.required">
                            <option value="" disabled>{{ option.placeholder }}</option>
                            <option v-for="value in option.values" :value="key(value)">{{ val(value) }}</option>
                        </ProSelect>
                    </div>
                </transition-group>
                <div class="support-form__buttons">
                    <ProButton class="previous" variant="muted" @click="back">{{ $t("support.previous_step") }}</ProButton>
                    <ProButton class="next" variant="primary" pill type="submit">{{ $t("support.next_step") }}</ProButton>
                </div>
            </div>

            <div v-if="step == 'details'" key="details" class="support-form__step support-form__details">
                <h3 class="support-form__step-title" v-html="selectedCategory.prompt"></h3>
                <ProInput
                    v-model="name"
                    :errors="errors.name"
                    :label="$t('support.name_label')"
                    type="text"
                    :placeholder="$t('support.name_placeholder')"
                    required
                    autocomplete="name"
                />
                <ProInput
                    v-model="email"
                    :errors="errors.email"
                    :label="$t('support.email_label')"
                    type="email"
                    :placeholder="$t('support.email_placeholder')"
                    required
                    autocomplete="email"
                />
                <ProInput
                    v-model="email_confirmation"
                    :errors="errors.email_confirmation"
                    ref="email_confirmation"
                    :label="$t('support.email_confirmation_label')"
                    type="email"
                    :placeholder="$t('support.email_confirmation_placeholder')"
                    required
                    autocomplete="email"
                />
                <SupportTextarea
                    label="Message"
                    label-hidden
                    v-model="message"
                    :errors="errors.message"
                    rows="6"
                    :placeholder="selectedCategory.message_placeholder"
                    :files="files"
                    @files="attachFiles"
                    required
                />
                <p v-if="errors.recaptcha" class="support-form__error">{{ errors.recaptcha[0] }}</p>
                <div class="support-form__buttons">
                    <ProButton class="previous" variant="muted" @click="back">{{ $t("support.previous_step") }}</ProButton>
                    <ProButton class="next" variant="primary" pill type="submit">{{ $t("global.send") }}</ProButton>
                </div>
            </div>

            <div v-if="step == 'confirmation' && sending" key="sending" class="support-form__step support-form__sending">
                <div class="support-form__loader loader" aria-hidden="true"></div>
                <h3 class="support-form__step-title" tabindex="-1">{{ $t("support.sending") }}</h3>
            </div>

            <div v-if="step == 'confirmation' && !sending" key="confirmation" class="support-form__step support-form__confirmation">
                <div class="support-form__success-icon" ref="success" :class="{ animate: !sending }" aria-hidden="true">
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 70 70"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        xml:space="preserve"
                        style="fill: currentColor; fill-rule: evenodd; clip-rule: evenodd; stroke-linejoin: round; stroke-miterlimit: 1.41421"
                        class="ng-scope"
                    >
                        <path
                            class="svg-path"
                            d="M33.633,44.076C33.633,44.076 31.535,45.802 30.251,46.859C30.066,47.011 29.813,47.043 29.598,46.942C29.383,46.841 29.241,46.623 29.234,46.382C29.178,44.697 29.086,41.944 29.086,41.944L33.633,44.076ZM27.285,38.671C27.112,38.77 26.903,38.779 26.721,38.694C25.814,38.268 22.936,36.919 20.73,35.884C20.27,35.669 19.982,35.191 20.001,34.674C20.02,34.158 20.341,33.703 20.815,33.523C27.078,31.145 42.051,25.459 48.314,23.08C48.788,22.899 49.322,23.03 49.666,23.408C50.01,23.788 50.098,24.342 49.886,24.811C47.708,29.671 43.294,39.518 41.503,43.513C41.296,43.975 40.917,44.334 40.45,44.512C39.985,44.688 39.469,44.669 39.016,44.457C36.696,43.37 32.628,41.463 30.843,40.625C30.65,40.535 30.515,40.35 30.485,40.135C30.454,39.921 30.533,39.704 30.693,39.561C32.937,37.57 39.56,31.692 39.56,31.692C39.56,31.692 29.13,37.624 27.285,38.671Z"
                        ></path>
                    </svg>
                </div>
                <h3 class="support-form__step-title text-heading text-heading--sm" tabindex="-1">{{ $t("support.thanks") }}</h3>
                <p class="text-body">
                    {{ selectedCategory.confirmation }}
                </p>
                <ProButton variant="primary" @click="reset">{{ $t("support.reset") }} <ProIcon icon="reset" /></ProButton>
            </div>
        </transition>

        <Alert ref="incompleteFilesModal" :title="$t('support.incomplete_files_modal_title')" :message="$t('support.incomplete_files_modal_message')" />

        <Alert
            ref="failedFilesModal"
            :title="$t('support.failed_files_modal_title')"
            :message="$t('support.failed_files_modal_message')"
            :action-title="$t('global.continue')"
            :cancellable="true"
        />

        <Alert ref="errorModal" :title="$t('support.error_modal_title')" :message="$t('support.error_modal_message')" />
    </form>
</template>

<script>
import ProSelect from "@/components/ProSelect";
import ProInput from "@/components/ProInput";
import SupportTextarea from "@/components/support/SupportTextarea";
import Alert from "@/components/Alert";

if (process.client) {
    var mojs = require("@mojs/core");
}

export default {
    components: { ProSelect, ProInput, SupportTextarea, Alert },
    props: {},
    data() {
        return {
            recaptcha_failed_to_load: false,
            sending: false,
            step: "category",
            selectedCategoryId: null,
            selectedProductId: null,
            currentTransition: "next",
            height: "auto",
            message: "",
            files: [],
            email: "",
            email_confirmation: "",
            name: "",
            version: "",
            device: "",
            os: "",
            browser: "",
            stylus: "",
            errors: {},
        };
    },
    computed: {
        categories() {
            return this.$t("support.categories");
        },
        products() {
            return this.$t("support.products");
        },
        selectedCategory() {
            return this.selectedCategoryId ? this.categories.find((category) => category.id == this.selectedCategoryId) : null;
        },
        selectedProduct() {
            return this.selectedProductId ? this.products.find((product) => product.id == this.selectedProductId) : null;
        },
    },
    methods: {
        next() {
            this.currentTransition = "next";

            // If there's a reCAPTCHA issue, stop progress and show an error.
            if (this.recaptcha_failed_to_load) {
                this.$refs.errorModal.open();
                return;
            }

            switch (this.step) {
                case "category":
                    if (!this.selectedCategory) break;

                    if (this.selectedCategory.product) {
                        this.step = "product";
                    } else {
                        this.step = "details";
                        this.selectedProductId = null; // Clear selected product data
                    }
                    break;
                case "product":
                    if (!this.selectedProduct) break;

                    this.step = "details";
                    break;
                case "details":
                    this.submit();
                    break;
            }
        },
        back() {
            this.currentTransition = "prev";

            switch (this.step) {
                case "product":
                    this.step = "category";
                    break;
                case "details":
                    if (this.selectedCategory.product) {
                        this.step = "product";
                    } else {
                        this.step = "category";
                    }
                    break;
                case "confirmation":
                    this.step = "details";
                    break;
            }
        },
        enterStep(el) {
            this.height = el.clientHeight + "px";
        },
        afterEnterStep(el) {
            this.height = "auto";

            if (this.errors) {
                // Find the first error and focus it
                this.$el
                    .querySelector(".input-group--has-errors input, .input-group--has-errors select, .input-group--has-errors textarea, .support-form-error")
                    ?.focus();
            } else {
                // Find the first tabindex element or input and focus it
                this.$el.querySelector('[tabindex="-1"],input,select')?.focus();
            }
        },
        beforeLeaveStep(el) {
            this.height = el.clientHeight + "px";
        },
        enterProductOption(el) {
            el.style.maxHeight = el.scrollHeight + "px";
        },
        beforeLeaveProductOption(el) {
            el.style.maxHeight = el.scrollHeight + "px";
        },
        key(val) {
            return Object.keys(val)[0];
        },
        val(val) {
            return Object.values(val)[0];
        },
        attachFiles(files) {
            this.files = files;
        },
        async submit() {
            if (this.files.filter((file) => file.failed).length) {
                if (!(await this.$refs.failedFilesModal.open())) return;
            }

            if (this.files.filter((file) => !file.failed && !file.complete).length) {
                this.$refs.incompleteFilesModal.open();
                return;
            }

            this.errors = {};
            this.step = "confirmation";
            this.sending = true;

            let recaptcha_token;
            try {
                recaptcha_token = await this.$recaptcha.execute("marketing/support_form/submit");
            } catch (e) {
                this.$refs.errorModal.open();
                this.$sentry.captureException(e);

                return this.back();
            }

            if (!recaptcha_token) {
                this.$refs.errorModal.open();
                return this.back();
            }

            try {
                const { data } = await this.$adminApi.post("/product-care/support-requests", {
                    category: this.selectedCategory.id,
                    product: this.selectedProduct ? this.selectedProduct.id : null,
                    version: this.version,
                    device: this.device,
                    os: this.os,
                    browser: this.browser,
                    stylus: this.stylus,
                    name: this.name,
                    email: this.email,
                    email_confirmation: this.email_confirmation,
                    message: this.message,
                    files: this.files.filter((file) => file.complete && !file.failed).map((file) => ({ name: file.name, key: file.key })),
                    recaptcha: recaptcha_token,
                });
            } catch (e) {
                if (e.response?.status == 422) {
                    this.errors = e.response.data.errors;
                } else {
                    this.$refs.errorModal.open();
                    this.$sentry.captureException(e);
                }

                this.back();
            }

            this.sending = false;
        },
        reset() {
            this.currentTransition = "prev";
            this.step = "category";
            this.selectedCategoryId = null;
            this.selectedProductId = null;
            this.message = "";
            this.files = [];
            this.email = "";
            this.email_confirmation = "";
            this.name = "";
            this.version = "";
            this.device = "";
            this.os = "";
            this.browser = "";
            this.stylus = "";
            this.errors = {};
        },
    },
    watch: {
        selectedProduct(product) {
            this.version = "";
            this.device = "";
            this.os = "";
            this.browser = "";
            this.stylus = "";
        },
        sending(val) {
            // Used for animation porpoises 🐬
            if (val) return;

            setTimeout(() => {
                window.burst = new mojs.Burst({
                    parent: this.$refs.success,
                    left: "50%",
                    top: "50%",
                    radius: { 70: 100 },
                    angle: 45,
                    isShowStart: true,
                    children: {
                        shape: "line",
                        radius: 10,
                        scale: 1,
                        stroke: "#0076ff",
                        strokeDasharray: "100%",
                        strokeDashoffset: { "-100%": "100%" },
                        duration: 700,
                        easing: "quad.out",
                    },
                }).play();
            }, 1000);
        },
        email_confirmation() {
            if (this.email != this.email_confirmation) {
                this.$refs.email_confirmation?.$refs.input.setCustomValidity(this.$t("support.email_confirmation_error"));
            } else {
                this.$refs.email_confirmation?.$refs.input.setCustomValidity("");
            }
        },
    },
    mounted() {
        if (this.$route.query.contact) {
            this.$nextTick(() => {
                this.$el.scrollIntoView({ behavior: "auto", block: "center" });
            });
        }

        this.$recaptcha.init().catch(() => {
            this.recaptcha_failed_to_load = true;
        });
    },
};
</script>
